import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ShieldX, Wrench, TrendingUp } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Chip, Button, Skeleton, EmptyState, Toast, Input } from "@/ui";

export default function Alerts() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [thr, setThr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/deck/alerts"); setData(r.data); setThr(String(Math.round((r.data.thresholdCentavos || 0) / 100))); }
    catch (e) { setError(friendlyError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2500); };
  const isOwner = data?.role === "owner";
  const saveThreshold = async () => {
    setSaving(true);
    try { await api.post("/deck/threshold", { thresholdCentavos: Math.round(parseFloat(thr || "0") * 100) }); flash("threshold saved"); load(); }
    catch (e) { setError(friendlyError(e)); } finally { setSaving(false); }
  };

  return (
    <Layout>
      <h1 className="t-h1">Alerts</h1>
      <p className="muted t-small mb-5">Refused assistant calls, failed setup steps, and shops whose cost per order runs high.</p>

      {error ? <Toast tone="bad" data-testid="alerts-error">{error}</Toast> : null}

      {!data ? <Skeleton style={{ height: 120 }} /> : (
        <>
          {data.canMoney ? (
            <Card className="p-4 mb-5" data-testid="threshold-card">
              <div className="flex items-center gap-2 text-sm font-semibold mb-2"><TrendingUp size={16} strokeWidth={1.75} />Assistant cost alert</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm" style={{ color: "var(--text-2)" }}>Warn me when a shop's cost per order is over ₱</span>
                <Input value={thr} data-testid="threshold-input" inputMode="decimal" disabled={!isOwner} style={{ width: 90 }}
                  onChange={(e) => setThr(e.target.value.replace(/[^0-9.]/g, ""))} />
                {isOwner ? <Button data-testid="threshold-save" loading={saving} onClick={saveThreshold}>save</Button> : <Chip tone="muted">set by owner</Chip>}
              </div>
            </Card>
          ) : null}

          {/* Cost over threshold */}
          {data.canMoney ? (
            <Section icon={TrendingUp} title="Cost per order running high" tone="warn" count={data.costAlerts.length} testid="cost-alerts">
              {data.costAlerts.length ? data.costAlerts.map((a) => (
                <Row key={a.shopId} testid={`cost-alert-${a.shopId}`}>
                  <span className="font-semibold">{a.shopName}</span>
                  <Chip tone="bad">{a.costPerOrder} / order</Chip>
                </Row>
              )) : <Empty>All shops are within your cost target.</Empty>}
            </Section>
          ) : null}

          {/* Failed setup steps */}
          <Section icon={Wrench} title="Failed setup steps" tone="bad" count={data.failedSteps.length} testid="failed-steps">
            {data.failedSteps.length ? data.failedSteps.map((f, i) => (
              <Row key={i} testid={`failed-step-${f.shopId}`}>
                <div><div className="font-semibold text-sm">{f.shopName}</div><div className="text-xs" style={{ color: "var(--bad)" }}>{f.step}{f.reason ? ` — ${f.reason}` : ""}</div></div>
              </Row>
            )) : <Empty>No setup steps are stuck.</Empty>}
          </Section>

          {/* Refused assistant calls */}
          {data.canMoney ? (
            <Section icon={ShieldX} title="Refused assistant calls" tone="warn" count={data.refusedCalls.length} testid="refused-calls">
              {data.refusedCalls.length ? data.refusedCalls.map((r, i) => (
                <Row key={i} testid={`refused-${i}`}>
                  <div><div className="font-semibold text-sm">{r.tool} <span style={{ color: "var(--text-3)" }}>({r.server})</span></div><div className="text-xs" style={{ color: "var(--text-3)" }}>{r.reason}</div></div>
                  <Chip tone="muted">{r.shopId}</Chip>
                </Row>
              )) : <Empty>No calls were refused.</Empty>}
            </Section>
          ) : null}
        </>
      )}

      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="alerts-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}

function Section({ icon: Icon, title, count, testid, children }) {
  return (
    <Card className="p-4 mb-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-sm font-semibold mb-2">
        <Icon size={16} strokeWidth={1.75} />{title}
        {count ? <Chip tone="bad">{count}</Chip> : <Chip tone="good">0</Chip>}
      </div>
      <div className="stack" style={{ gap: 8 }}>{children}</div>
    </Card>
  );
}
function Row({ children, testid }) {
  return <div className="flex items-center justify-between gap-2 rounded-xl px-3 py-2" style={{ background: "#f8fafc" }} data-testid={testid}>{children}</div>;
}
function Empty({ children }) {
  return <div className="text-sm" style={{ color: "var(--text-3)" }}>{children}</div>;
}
